export const userData = {
  name: 'Marcus Vance',
  role: 'Fibre Optic Splicer II',
  level: 4,
  xp: 3450,
  nextLevelXp: 5000,
  streak: 12,
  casesSolved: 142,
  company: 'NexFiber Telecom',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&auto=format&fit=crop'
};

export const modulesData = [
  { id: 'm1', track: 'cft', title: 'Fiber Fundamentals', duration: '12 min', xp: 100, completed: true, summary: 'Core principles of light transmission in optical fibers.' },
  { id: 'm2', track: 'cft', title: 'Connector Cleaning Basics', duration: '8 min', xp: 50, completed: true, summary: 'Proper techniques for inspecting and cleaning LC/SC connectors.' },
  { id: 'm3', track: 'cft', title: 'Splicing 101', duration: '15 min', xp: 150, completed: false, summary: 'Setup, cleaving, and operating a fusion splicer safely.' },
  { id: 'm4', track: 'cft', title: 'Reading Loss and Signal Issues', duration: '20 min', xp: 200, completed: false, summary: 'Interpreting OTDR traces and identifying microbends.' },
  { id: 'm5', track: 'cfm', title: 'Escalation Best Practices', duration: '10 min', xp: 100, completed: false, summary: 'When and how to escalate complex field issues to Tier 3.' },
  { id: 'm6', track: 'cfm', title: 'Team Quality Control', duration: '15 min', xp: 150, completed: false, summary: 'Auditing splice enclosures and cabinet organization.' },
];

export const certsData = [
  { id: 'cft', title: 'Certified Fiber Technician', progress: 65, totalModules: 12, completedModules: 8, locked: false },
  { id: 'cfm', title: 'Certified Fiber Manager', progress: 10, totalModules: 8, completedModules: 1, locked: true },
];

export const caseHistoryData = [
  { id: 'CASE-8902', date: 'Today, 09:42 AM', issue: 'High Insertion Loss', status: 'resolved', image: 'https://images.unsplash.com/photo-1558227691-41ea78d1f631?q=80&w=100&auto=format&fit=crop' },
  { id: 'CASE-8895', date: 'Yesterday, 14:15 PM', issue: 'Damaged Cable Jacket', status: 'escalated', image: 'https://images.unsplash.com/photo-1563207153-f403bf289096?q=80&w=100&auto=format&fit=crop' },
  { id: 'CASE-8850', date: 'Oct 12, 11:00 AM', issue: 'Dirty LC Connector', status: 'resolved', image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=100&auto=format&fit=crop' },
];

export const issueCategories = [
  'Bad Splice', 'Dirty Connector', 'Excessive Bend Radius', 'Damaged Cable Jacket', 
  'Weak Signal / High Loss', 'Cabinet Organization', 'Labeling Issue', 'Failed Termination', 'Unknown'
];

export const supervisors = [
  { id: 'sup1', name: 'Sarah Jenkins', role: 'Tier 3 Support Lead', status: 'online', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&auto=format&fit=crop' },
  { id: 'sup2', name: 'David Chen', role: 'Field Operations Mgr', status: 'busy', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=150&auto=format&fit=crop' },
];

// Simulated AI Logic Engine
export const simulateAdamAI = (category, answers) => {
  // Base response structure
  const response = {
    diagnosis: '',
    confidence: 0,
    summary: '',
    nextChecks: [],
    video: { title: '', duration: '', desc: '', thumb: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=400&auto=format&fit=crop' },
    guide: { cause: '', tools: [], steps: [], warning: '' }
  };

  if (category === 'Dirty Connector' || answers.symptom?.includes('Intermittent')) {
    response.diagnosis = 'Contamination on End-Face (LC/SC)';
    response.confidence = 94;
    response.summary = 'Visual analysis of the uploaded scope image strongly indicates dust or oil particles on the fiber core, causing high reflectance and signal degradation.';
    response.nextChecks = ['Check mating adapter for cross-contamination', 'Verify optical power levels post-cleaning'];
    response.video = { title: 'Wet-to-Dry Cleaning Technique', duration: '4:15', desc: 'Proper method for removing stubborn oils from ferrules.' };
    response.guide = {
      cause: 'Dust, skin oil, or improper dust cap usage.',
      tools: ['Click Cleaner', 'Lint-free wipes', 'Fiber optic cleaning solvent'],
      warning: 'Never look directly into an active fiber. Always use a scope.',
      steps: [
        'Perform a dry clean using a click-cleaner (1-2 clicks).',
        'Re-inspect with fiber scope.',
        'If still dirty, apply 1 drop of solvent to a lint-free wipe.',
        'Swipe ferrule end-face across the wet area, then immediately across a dry area in a figure-8 motion.',
        'Re-inspect and reconnect immediately.'
      ]
    };
  } else if (category === 'Bad Splice' || category === 'Weak Signal / High Loss') {
    response.diagnosis = 'High-Loss Fusion Splice / Core Misalignment';
    response.confidence = 88;
    response.summary = 'The parameters and visual characteristics suggest a bad cleave angle or dirt in the v-groove caused a poor alignment during the arc cycle.';
    response.nextChecks = ['Check cleaver blade lifecycle', 'Clean splicer v-grooves and mirrors'];
    response.video = { title: 'Troubleshooting High Loss Splices', duration: '6:30', desc: 'Identifying off-axis and bubble splices.' };
    response.guide = {
      cause: 'Poor cleave (angle > 1 degree) or contaminated splicer components.',
      tools: ['Cleaver', 'Isopropyl Alcohol', 'Cotton Swabs', 'Strippers'],
      warning: 'Ensure arc power is calibrated for the specific fiber type (SMF/MMF).',
      steps: [
        'Break the bad splice safely and dispose of fiber shards in a shard catcher.',
        'Re-strip at least 2 inches of coating from both ends.',
        'Clean bare fiber thoroughly with alcohol (listen for the squeak).',
        'Re-cleave and check cleave angles on splicer display (must be < 0.5°).',
        'Re-splice and verify estimated loss is < 0.02dB.'
      ]
    };
  } else {
    // Generic fallback
    response.diagnosis = 'Physical Layer Anomaly Detected';
    response.confidence = 72;
    response.summary = 'AdamAI detected irregularities in the provided data. A physical inspection of the immediate cable path and termination points is required.';
    response.nextChecks = ['Perform VFL (Visual Fault Locator) test', 'Check for microbends in trays'];
    response.video = { title: 'Basic VFL Troubleshooting', duration: '3:45', desc: 'Using red light to find breaks.' };
    response.guide = {
      cause: 'Unknown physical stress or termination failure.',
      tools: ['VFL (Visual Fault Locator)', 'OTDR'],
      warning: 'Do not bend fiber beyond its specified bend radius during inspection.',
      steps: [
        'Connect VFL to the dark fiber port.',
        'Trace the path visually looking for red light leakage.',
        'Check all nearby splice trays for pinched tubes.',
        'If no visual fault found, escalate to OTDR testing.'
      ]
    };
  }
  return response;
};